import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, resolveDynamicComponent as _resolveDynamicComponent, toHandlers as _toHandlers, renderList as _renderList, createSlots as _createSlots, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "text-muted text-small"
}

import {QCheckbox, QFile, QInput, QSelect} from 'quasar';
import ApiPasswordInput from 'src/components/qutils/form/ApiPasswordInput.vue';
import { computed, PropType, ref, useAttrs, watch } from 'vue';

/**
 * Interface for field options used in choice fields
 */
export interface FieldOption {
  value: string;
  display_name: string;
}

/**
 * Interface for form field configuration
 */
export interface Field {
  type: string;
  required?: boolean;
  read_only?: boolean;
  label?: string;
  children?: Array<Field>;
  choices?: Array<FieldOption>;
  max_length?: number;
  help_text?: string;
  default?: any;
}

/**
 * Interface for component configuration in TYPE_MAPPING
 */
interface ComponentMapping {
  component: any;
  attrs?: Record<string, any>;
}

/**
 * Interface for processed component configuration
 */
interface ProcessedComponent {
  component: any;
  attrs: Record<string, any>;
  handlers: Record<string, any>;
}

/**
 * Interface for select option after processing
 */
interface SelectOption {
  label: string;
  value: string;
}

/**
 * Mapping of field types to their corresponding Quasar components and default attributes
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DynamicInput',
  props: {
    field: {
      required: false,
      type: Object as PropType<Field>,
    },
    fieldComponent: {
      default: undefined,
      type: Object,
    },
    /**
     * Errors can be either:
     * - Array of error messages (string[])
     * - Boolean indicating error state (true/false)
     * - undefined (no errors)
     */
    errors: {
      default: undefined,
      type: [Array, Boolean] as PropType<string[] | boolean>,
    },
    modelValue: {
      required: true,
    },
    isFilterable: {
      default: false,
      type: Boolean
    }
},
  emits: ['update:modelValue'],
  setup(__props) {

const TYPE_MAPPING: Record<string, ComponentMapping | undefined> = {
  field: undefined,
  boolean: {
    component: QCheckbox,
    attrs: {},
  },
  string: {
    component: QInput,
    attrs: {
      type: 'text',
    },
  },
  url: {
    component: QInput,
    attrs: {
      type: 'url',
    },
  },
  email: {
    component: QInput,
    attrs: {
      type: 'email',
    },
  },
  password: {
    component: ApiPasswordInput,
    attrs: {
      type: 'password',
    },
  },
  regex: {
    component: QInput,
  },
  slug: {
    component: QInput,
  },
  integer: {
    component: QInput,
    attrs: {
      type: 'number',
    },
  },
  float: {
    component: QInput,
    attrs: {
      type: 'number',
    },
  },
  decimal: {
    component: QInput,
    attrs: {
      type: 'number',
    },
  },
  date: {
    component: QInput,
    attrs: {
      type: 'date',
    },
  },
  datetime: {
    component: QInput,
    attrs: {
      type: 'date',
    },
  },
  time: {
    component: QInput,
    attrs: {
      type: 'time',
    },
  },
  choice: {
    component: QSelect,
    attrs: {
      'emit-value': true,
      'map-options': true,
    }
  },
  'multiple choice': {
    component: undefined,
  },
  'file upload': {
    component: QFile,
  },
  'image upload': {
    component: QFile,
  },
  list: {
    component: undefined,
  },
  'nested object': {
    component: undefined,
  },
};

/**
 * Component props definition with proper TypeScript types
 */
const props = __props

/**
 * Component emits definition
 */


const $attrs = useAttrs();

/**
 * Computed property for select options, optimized for reactivity
 */
const options = computed<SelectOption[]>(() => {
  return props?.field?.choices?.map((obj) => ({
    label: obj.display_name,
    value: obj.value,
  })) ?? [];
});

/**
 * Ref for filtered options when using filterable select
 */
const filteredOptions = ref<SelectOption[]>([]);

// Initialize filtered options with all options
watch(options, (newOptions) => {
  if (props.isFilterable) {
    filteredOptions.value = newOptions;
  }
}, { immediate: true });

/**
 * Creates enhanced label with required indicator
 * @param label - Base label text
 * @param required - Whether field is required
 * @returns Enhanced label string
 */
const createEnhancedLabel = (label: string | undefined, required: boolean | undefined): string | undefined => {
  if (!label) return label;
  return required ? `${label} *` : label;
};

/**
 * Main computed property that builds the component configuration
 * based on field type and props
 */
const component = computed(() => {
  // Early return for fieldComponent override
  if (props.fieldComponent) {
    return props.fieldComponent;
  }

  // Validation checks with proper error handling
  if (!props.field) {
    console.warn('[DynamicInput] No field configuration provided');
    return { component: null, attrs: {}, handlers: {} };
  }

  if (!props.field.type) {
    console.warn('[DynamicInput] Field type not specified');
    return { component: null, attrs: {}, handlers: {} };
  }

  const mapping = TYPE_MAPPING[props.field.type];
  if (!mapping) {
    console.warn(`[DynamicInput] Unsupported field type: "${props.field.type}"`);
    return { component: null, attrs: {}, handlers: {} };
  }

  let r: ProcessedComponent = {
    component: mapping.component,
    attrs: {
      ...mapping.attrs,
      label: createEnhancedLabel(props.field.label, props.field.required),
      required: props.field.required,
      hint: props.field.help_text,
      readonly: props.field.read_only,
      maxlength: props.field.max_length,
      behavior: 'menu',
      modelValue: props.modelValue ?? props.field.default,
      ...$attrs,
    },
    handlers: {},
  };

  // Remove the manual label enhancement since it's now handled by createEnhancedLabel
  // if (r.attrs.required) {
  //   r['attrs']['label'] = r['attrs']['label'] + ' *';
  // }

  if (r.component === QSelect) {
    if (props.isFilterable) {
      r.attrs['filterFn'] = (val: string, update: (fn: () => void, after?: (ref: any) => void) => void) => {
        if (val === '') {
          update(() => {
            filteredOptions.value = options.value;
          });
          return;
        }

        update(() => {
          const needle = val.toLowerCase();
          filteredOptions.value = options.value.filter(v => v.label.toLowerCase().indexOf(needle) > -1);
        }, ref => {
          if (val !== '' && ref.options.length > 0) {
            ref.setOptionIndex(-1); // reset optionIndex in case there is something selected
            ref.moveOptionSelection(1, true); // focus the first selectable option and do not update the input-value
          }
        });
      };

      r.attrs['use-input'] = true;
      r.attrs['clearable'] = true;
      r.attrs['required'] = false;
    }

    if (r.attrs['modelValue'] === undefined) {
      r.attrs['modelValue'] = null;
    }
  }

  if (r.component === QCheckbox) {
    r.attrs.class = !r.attrs?.class ? 'q-py-sm' : r.attrs.class;
    if ($attrs['no-indeterminate']) {
      r.attrs['modelValue'] = r.attrs['modelValue'] || false;
    }
  }

  r.attrs = {
    ...r.attrs,
    ...$attrs,
  };

  return r;
});


return (_ctx: any,_cache: any) => {
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_item = _resolveComponent("q-item")!

  return (_openBlock(), _createElementBlock("div", null, [
    (__props.field)
      ? (_openBlock(), _createBlock(_resolveDynamicComponent(component.value.component), _mergeProps({
          key: 0,
          error: Array.isArray(__props.errors) ? (__props.errors.length > 0) : !!__props.errors,
          "error-message": Array.isArray(__props.errors) && __props.errors.length > 0 ? __props.errors.join(' ') : undefined,
          "model-value": component.value.attrs.modelValue,
          options: component.value.component === _unref(QSelect) ? (props.isFilterable ? filteredOptions.value : options.value) : undefined
        }, component.value.attrs, _toHandlers(component.value.handlers), {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = 
        (val) => {
          _ctx.$emit('update:modelValue', val);
          component.value.attrs.modelValue = val;
        }
      ),
          onFilter: component.value.attrs?.filterFn
        }), _createSlots({ _: 2 }, [
          _renderList(_ctx.$slots, (_, name) => {
            return {
              name: name,
              fn: _withCtx((data) => [
                _renderSlot(_ctx.$slots, name, { data: data })
              ])
            }
          }),
          (component.value.component?.name === 'QSelect')
            ? {
                name: "option",
                fn: _withCtx(({ itemProps, opt, selected }) => [
                  _createVNode(_component_q_item, _mergeProps(itemProps, { "active-class": "bg-grey-3" }), {
                    default: _withCtx(() => [
                      _createVNode(_component_q_item_section, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_q_item_label, null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(opt.label), 1)
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _: 2
                      }, 1024),
                      _createVNode(_component_q_item_section, { side: "" }, {
                        default: _withCtx(() => [
                          selected
                            ? (_openBlock(), _createBlock(_component_q_icon, {
                                key: 0,
                                name: "done"
                              }))
                            : _createCommentVNode("", true)
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 2
                  }, 1040)
                ]),
                key: "0"
              }
            : undefined
        ]), 1040, ["error", "error-message", "model-value", "options", "onFilter"]))
      : _createCommentVNode("", true),
    (component.value.component?.name === 'QCheckbox' && component.value.attrs.hint)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(component.value.attrs.hint), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})